import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import styles from "./UATFieldsForm.module.scss";
import classNames from "classnames/bind";
import UATStatusSelect from "../selects/UATStatusSelect/UATStatusSelect";
import { useController, useForm } from "react-hook-form";
import { EDataKeys, TData } from "../../types/TData";
import DatePicker from "../DatePicker/DatePicker.com";
import SlideCheckbox from "../SlideCheckbox/SlideCheckbox.com";
import BasicTextarea from "../BasicTextarea/BasicTextarea.com";
import Select from "../SelectNew/Select";
import { delayOwnershipReasonOptions, delayReasonOptions } from "../../constants";
import { useUpdateProjectMutation, addMessage } from "../../store/services";
import { debounce } from "../../helpers";

const cx = classNames.bind(styles);

function UATFieldsForm({ project }: Props) {
    const [updateProject] = useUpdateProjectMutation();
    const dispatch = useDispatch();

    // Parse API response and show appropriate message
    const handleUpdateResponse = useCallback((fieldName: string, response: any) => {
        if (response?.data && Array.isArray(response.data)) {
            const result = response.data[0];
            if (result?.status === 200) {
                dispatch(addMessage({ type: 'success', text: `${fieldName} updated successfully` }));
            } else if (result?.status === 400 && result?.errors) {
                const errorMessages = result.errors.map((err: any) => err.message).join(', ');
                dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: ${errorMessages}` }));
            }
        } else if (response?.error) {
            dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: Network error` }));
        }
    }, [dispatch]);

    // Helper function to handle updates with message feedback
    const handleFieldUpdate = (payload: any, fieldName: string) => {
        updateProject(payload)
            .then((response) => {
                handleUpdateResponse(fieldName, response);
            })
            .catch((error) => {
                console.error(`Failed to update ${fieldName}:`, error);
                dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: Network error` }));
            });
    };

    const debouncedUpdateProject = useMemo(() => {
        const debouncedFn = debounce((payload: any, fieldName: string) => {
            updateProject(payload)
                .then((response) => {
                    handleUpdateResponse(fieldName, response);
                })
                .catch((error) => {
                    console.error('Debounced update failed:', error);
                    dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: Network error` }));
                });
        }, 500);
        return debouncedFn;
    }, [updateProject, dispatch, handleUpdateResponse]);
    const { control, reset } = useForm<FormValues>({
        defaultValues: {
            uatStatus: project[EDataKeys.UAT_STATUS] || "",
            uatDate: project[EDataKeys.UAT_DATE] ? new Date(project[EDataKeys.UAT_DATE]) : null,
            supportChargeStarted: project[EDataKeys.SUPPORT_CHARGE_STARTED] || false,
            uatNote: project[EDataKeys.UAT_NOTE] || "",
            delayOwnershipReason: project[EDataKeys.DELAY_OWNERSHIP] || "",
            delayReason: project[EDataKeys.DELAY_REASON] || ""
        },
        mode: "onChange",
    });

    useEffect(() => {
        reset({
            uatStatus: project[EDataKeys.UAT_STATUS] || "",
            uatDate: project[EDataKeys.UAT_DATE] ? new Date(project[EDataKeys.UAT_DATE]) : null,
            supportChargeStarted: project[EDataKeys.SUPPORT_CHARGE_STARTED] || false,
            uatNote: project[EDataKeys.UAT_NOTE] || "",
            delayOwnershipReason: project[EDataKeys.DELAY_OWNERSHIP] || "",
            delayReason: project[EDataKeys.DELAY_REASON] || ""
        });
    }, [project, reset])

    const {
        field: uatStatus, //
    } = useController({
        name: "uatStatus",
        control,
    });

    const {
        field: uatDate, //
    } = useController({
        name: "uatDate",
        control,
    });

    const {
        field: supportChargeStarted,
    } = useController({
        name: "supportChargeStarted",
        control,
    });

    const {
        field: uatNote,
    } = useController({
        name: "uatNote",
        control,
    });

    const {
        field: delayOwnershipReason,
    } = useController({
        name: "delayOwnershipReason",
        control,
    });

    const {
        field: delayReason,
    } = useController({
        name: "delayReason",
        control,
    });

    return (
        <form className={cx("uat-form")}>
            <div className={cx("row")}>
                <UATStatusSelect
                    onChange={(newValue) => {
                        uatStatus.onChange(newValue);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.UAT_STATUS]: newValue || "" }], "UAT Status");
                    }}
                    value={uatStatus.value}
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="uat-date">UAT Date</label>
                <DatePicker
                    onChange={(newDate) => {
                        uatDate.onChange(newDate);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.UAT_DATE]: (newDate as Date)?.toISOString() || null }], "UAT Date");
                    }}
                    date={uatDate.value}
                    placeholder="UAT Date"
                    id="uat-date"
                />

            </div>

            <div className={cx("row", "toggle")}>
                <SlideCheckbox
                    checked={supportChargeStarted.value}
                    onChange={(checked) => {
                        supportChargeStarted.onChange(checked);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.SUPPORT_CHARGE_STARTED]: checked }], "Support Charge Started");
                    }}
                    renderLabel={() => "Support Charge Started"}
                />
            </div>

            <div className={cx("row")}>
                <BasicTextarea
                    label="UAT Note"
                    onChange={(value) => {
                        uatNote.onChange(value);
                        debouncedUpdateProject([{ Id: project.Id, [EDataKeys.UAT_NOTE]: value || "" }], "UAT Note")
                    }}
                    value={uatNote.value}
                    className={cx("uat-note")}
                    placeholder="Type UAT Note"
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="delay-ownership-reason">Delay Ownership Reason</label>
                <Select
                    placeholder="Select Delay Ownership Reason"
                    options={delayOwnershipReasonOptions.map((item) => ({
                        value: item,
                        label: item
                    }))}
                    onChange={(value) => {
                        delayOwnershipReason.onChange(value);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.DELAY_OWNERSHIP]: value as string || "" }], "Delay Ownership Reason");
                    }}
                    value={delayOwnershipReason.value}
                    isClearable
                    id="delay-ownership-reason"
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="delay-reason">Delay Reason</label>
                <Select
                    placeholder="Select Delay Reason"
                    options={delayReasonOptions.map((item) => ({
                        value: item,
                        label: item
                    }))}
                    onChange={(value) => {
                        delayReason.onChange(value);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.DELAY_REASON]: value as string || "" }], "Delay Reason");
                    }}
                value={delayReason.value}
                isClearable
                id="delay-reason"
                />
            </div>
        </form>
    );
}

type FormValues = {
    uatStatus: string,
    uatDate: Date | null,
    uatNote: string,
    supportChargeStarted: boolean,
    delayOwnershipReason: string,
    delayReason: string,
}

type Props = {
    project: TData
}

export default UATFieldsForm;